import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Container } from 'components/Container'
import { PageTitle } from 'components/PageTitle'
import { useDispatch, useSelector } from 'react-redux'
import { eventLogger } from 'services/eventLogger.service'
import { sendUserInfoAction } from 'root-redux/actions/user'
import { resetErrorAction } from 'root-redux/actions/common'
import { selectError } from 'root-redux/selects/common'
import { selectUserStatus, selectUUID } from 'root-redux/selects/user'
import { useLocation } from 'react-router'
import { useEmailInputField } from 'hooks/useEmailInputField'

import { PageId } from 'page-constants'
import { Button } from 'components/Button'
import { ErrorNotification } from 'components/ErrorNotification'
import { Modal } from 'components/Modal'

import lockIconSvg from 'assets/images/sprite/lock.svg'

import { googleAnalyticsLogger } from 'services/googleAnalytics.service'
import { goTo } from '../../browserHistory'
import { StyledEmail as S } from './Email.styles'

export const Email: React.FC = () => {
  const { t } = useTranslation()
  const { search } = useLocation()
  const dispatch = useDispatch()

  const userStatus = useSelector(selectUserStatus)
  const uuid = useSelector(selectUUID)
  const error = useSelector(selectError)

  const [isErrorModalShown, setIsErrorModalShown] = useState(false)
  const [email, setEmail] = useEmailInputField()

  const isEmailValid = useMemo(() => email.isValid, [email.isValid])

  const errorText = useMemo(
    () => (!email.isValid ? email.validationText : ''),
    [email.isValid, email.validationText],
  )

  useEffect(() => {
    eventLogger.logEmailPageShown()
  }, [])

  useLayoutEffect(() => {
    if (userStatus?.email.hasEmail) {
      goTo({ pathname: PageId.RESULT, search })
    }
  }, [userStatus?.email.hasEmail, search])

  const handleErrorModal = useCallback(() => setIsErrorModalShown(true), [])

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault()
      if (!email.isValid) return

      goTo({ pathname: PageId.RESULT, search })
      dispatch(sendUserInfoAction(email.value, handleErrorModal))

      googleAnalyticsLogger.logEnterEmailPageCompleted()
      eventLogger.logEmailPageCompleted({ email: email.value })
      window.fbq('track', 'Lead', {}, { eventID: uuid })
    },
    [dispatch, uuid, email.value, email.isValid, handleErrorModal, search],
  )

  const handleChange = useCallback(
    ({ target: { value } }) => {
      const emailValue = value.toLowerCase().trim()

      setEmail((prevState) => ({
        ...prevState,
        value: emailValue,
      }))
    },
    [setEmail],
  )

  const handleFocus = useCallback(() => {
    setEmail((prevState) => ({
      ...prevState,
      isFocused: true,
    }))
  }, [setEmail])

  const handleBlur = useCallback(() => {
    setEmail((prevState) => ({
      ...prevState,
      isFocused: false,
    }))
  }, [setEmail])

  return (
    <div>
      <Container>
        <S.TitleContainer>
          <PageTitle marginBottom={24}>
            <Trans i18nKey="onboarding.email.title" components={[<strong />]} />
          </PageTitle>
        </S.TitleContainer>
        <form onSubmit={handleSubmit}>
          <S.Input
            value={email.value}
            isValid={isEmailValid}
            maxLength={50}
            label={t('onboarding.email.placeholder')}
            shortLabel={t('onboarding.email.shortPlaceholder')}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          <S.Disclaimer>
            <S.LockIcon svg={lockIconSvg} width={16} />
            {t('onboarding.email.disclaimer')}
          </S.Disclaimer>
          <S.ButtonContainer>
            <ErrorNotification errorText={errorText} />
            <Button type="submit" disabled={!email.value || !isEmailValid}>
              {t('actions.seeResults')}
            </Button>
          </S.ButtonContainer>
        </form>
      </Container>
      <Modal
        onClose={() => {
          dispatch(resetErrorAction())
          setIsErrorModalShown(false)
        }}
        isShown={isErrorModalShown}
        error={error}
      />
    </div>
  )
}
